
import { useForm, SubmitHandler } from 'react-hook-form';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

type FormData = {
  nombre: string;
  email: string;
  telefono: string;
  condominio: string;
};

const ContactFormSection = () => {
  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormData>();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke('send-management-request', {
        body: data,
      });

      if (error) {
        throw new Error(error.message);
      }

      toast({
        title: "¡Información enviada!",
        description: "Gracias, hemos recibido tus datos. Pronto nos pondremos en contacto.",
      });
      reset();
    } catch (error) {
      console.error("Error submitting form:", error);
      toast({
        title: "Error al enviar",
        description: "No se pudo enviar tu información. Por favor, inténtalo de nuevo.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            ¿Quieres saber más?
          </h2>
          <p className="text-xl text-gray-600">
            Deja tus datos para obtener atención personalizada
          </p>
        </div>
        
        <Card className="shadow-lg">
          <CardContent className="p-8">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="nombre" className="text-gray-700 font-medium">
                  Nombre completo
                </Label>
                <Input
                  id="nombre"
                  type="text"
                  {...register("nombre", { required: "Este campo es requerido" })}
                  placeholder="Tu nombre completo"
                  className="h-12"
                />
                {errors.nombre && (
                  <span className="text-red-500 text-sm">{errors.nombre.message}</span>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700 font-medium">
                  Correo electrónico
                </Label>
                <Input
                  id="email"
                  type="email"
                  {...register("email", { 
                    required: "Este campo es requerido",
                    pattern: {
                      value: /^\S+@\S+$/i,
                      message: "Correo electrónico inválido"
                    }
                  })}
                  placeholder="tu@email.com"
                  className="h-12"
                />
                {errors.email && (
                  <span className="text-red-500 text-sm">{errors.email.message}</span>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="telefono" className="text-gray-700 font-medium">
                  Teléfono
                </Label>
                <Input
                  id="telefono"
                  type="tel"
                  {...register("telefono", { required: "Este campo es requerido" })}
                  placeholder="Ej. 333 123 4567"
                  className="h-12"
                />
                {errors.telefono && (
                  <span className="text-red-500 text-sm">{errors.telefono.message}</span>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="condominio" className="text-gray-700 font-medium">
                  Nombre del condominio
                </Label>
                <Input
                  id="condominio"
                  type="text"
                  {...register("condominio")}
                  placeholder="Ej. Condominio Mar Azul"
                  className="h-12"
                />
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                size="lg"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Enviando...' : 'Enviar información'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default ContactFormSection;
